import { storeToRefs } from "pinia";
import { checkFbsPermission } from "@/shared/helper/fbsPermissions.helper";
import type { RouteLocationNormalizedGeneric } from "vue-router";
import { AppIdentifier } from "@/shared/@types/AppIdentifier";
import { AppRouteNames } from "@/shared/@types/AppRouteNames";
import type { FbsCustomerType } from "@/app/fbs-hub-app/shared/@types/FbsCustomerType";
import { useAuthenticatedUsersOnly } from "@/shared/router/useAuthenticatedUsersOnly.composable";
import { useGasContainerStore } from "@/app/fbs-hub-app/shared/stores/GasContainerStore";

export const redirectToAuthenticatedMeterReportIfPermitted = async (
  to: RouteLocationNormalizedGeneric,
) => {
  const { fbsCustomerType, gasContainerHasMeterContract } = await getParams(to);
  return canAccessAuthenticatedMeterReport(
    fbsCustomerType.value,
    gasContainerHasMeterContract.value,
  )
    ? { name: AppRouteNames.FbsHubMeterReport }
    : true;
};

export const redirectToUnauthenticatedMeterReportIfNotPermitted = async (
  to: RouteLocationNormalizedGeneric,
) => {
  const { fbsCustomerType, gasContainerHasMeterContract } = await getParams(to);
  return !canAccessAuthenticatedMeterReport(
    fbsCustomerType.value,
    gasContainerHasMeterContract.value,
  )
    ? { name: AppRouteNames.MeterReport }
    : true;
};

const canAccessAuthenticatedMeterReport = (
  fbsCustomerType: FbsCustomerType | undefined,
  gasContainerHasMeterContract: boolean,
) =>
  checkFbsPermission(
    AppRouteNames.FbsHubMeterReport,
    fbsCustomerType,
    gasContainerHasMeterContract,
  );

const getParams = async (to: RouteLocationNormalizedGeneric) => {
  const { hasUserData, initializeUserData } = useAuthenticatedUsersOnly(to);
  if (!hasUserData.value) await initializeUserData(AppIdentifier.FbsHub);

  const gasContainerStore = useGasContainerStore();
  const { gasContainers, fbsCustomerType, gasContainerHasMeterContract } =
    storeToRefs(gasContainerStore);

  if (!gasContainers.value.length) await gasContainerStore.fetchGasContainers();

  return { fbsCustomerType, gasContainerHasMeterContract };
};
